<template>
  <div class="report-content-editor">
    <div class="batch-insert-bar">
      <input
        ref="fileInput"
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp"
        multiple
        style="display: none"
        @change="handleFilesChange"
      >
      <el-button size="small" icon="el-icon-picture-outline" :disabled="busy" :loading="processingFiles" @click="openFilePicker">
        {{ processingFiles ? '正在校验图片…' : '选择图片（可多选）' }}
      </el-button>
      <el-radio-group v-model="sortBy" size="small" style="margin-left: 10px;" :disabled="busy">
        <el-radio-button label="name">按文件名排序</el-radio-button>
        <el-radio-button label="time">按修改时间排序</el-radio-button>
      </el-radio-group>
      <el-button
        type="primary"
        size="small"
        style="margin-left: 10px;"
        :disabled="!items.length || processingFiles"
        :loading="uploading"
        @click="uploadAndInsert"
      >{{ uploadButtonText }}</el-button>
    </div>
    <ul v-if="items.length" class="pending-file-list">
      <li v-for="(item, index) in sortedItems" :key="`${item.file.name}_${item.file.lastModified}_${index}`">
        <span class="file-order">{{ index + 1 }}.</span>
        <span class="file-name">{{ item.file.name }}</span>
        <span class="file-size">({{ formatSize(item.file.size) }})</span>
        <span v-if="item.status === 'uploading'" class="file-status uploading">上传中…</span>
        <span v-else-if="item.status === 'done'" class="file-status done">已上传</span>
        <span v-else-if="item.status === 'failed'" class="file-status failed" :title="item.error">失败，点上传按钮重试</span>
        <el-button v-if="!busy" type="text" icon="el-icon-close" @click="removeItem(index)" />
      </li>
    </ul>
    <bean-ck-editor v-if="format === 'html'" :value="normalizedValue" @change="handleEditorChange" />
    <bean-markdown-editor v-else :value="normalizedValue" @change="handleEditorChange" />
  </div>
</template>

<script>
  import { Vue, Component, Model, Prop } from 'vue-property-decorator';
  import { uploadFile } from 'beans-admin-plugin';
  import _ from 'lodash';

  const MAX_FILE_SIZE = 20 * 1024 * 1024; // 单文件 20MB
  const MAX_BATCH_SIZE = 30; // 单次最多 30 张
  const MAX_TOTAL_SIZE = 100 * 1024 * 1024; // 单批总量 100MB
  const DECODE_TIMEOUT = 10 * 1000; // 图片解码预检超时 10 秒
  const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

  @Component
  export default class ReportContentEditor extends Vue {
    @Model('change', { type: String, default: '' }) value;
    // html: 插入 <img> 标签；markdown: 插入 ![](url) 语法
    @Prop({ type: String, default: 'html', validator: v => ['html', 'markdown'].includes(v) }) format;

    // 每项: { file, status: 'pending'|'uploading'|'done'|'failed', url, error }
    items = [];
    sortBy = 'name';
    uploading = false;
    // 图片解码校验是异步的，校验期间同样锁定全部控件，避免“校验未完成就点上传”的竞态
    processingFiles = false;

    created() {
      window.addEventListener('beforeunload', this.handleBeforeUnload);
    }

    beforeDestroy() {
      window.removeEventListener('beforeunload', this.handleBeforeUnload);
    }

    // 上传中、校验中或存在未插入的文件时，关闭/刷新页面前拦截提醒
    handleBeforeUnload(e) {
      if (this.busy || this.items.length) {
        e.preventDefault();
        e.returnValue = '';
      }
    }

    get busy() {
      return this.uploading || this.processingFiles;
    }

    get normalizedValue() {
      // 数据库字段允许 NULL，编辑器只接受字符串
      return typeof this.value === 'string' ? this.value : '';
    }

    get sortedItems() {
      const items = [...this.items];
      if (this.sortBy === 'time') {
        return items.sort((a, b) => a.file.lastModified - b.file.lastModified ||
          a.file.name.localeCompare(b.file.name, 'zh-CN', { numeric: true, sensitivity: 'base' }));
      }
      // 自然排序：2.png 排在 10.png 前面
      return items.sort((a, b) => a.file.name.localeCompare(b.file.name, 'zh-CN', { numeric: true, sensitivity: 'base' }));
    }

    get uploadButtonText() {
      if (this.items.some(item => item.status === 'failed')) return '重试失败项并插入';
      return '上传并插入到内容末尾';
    }

    openFilePicker() {
      if (this.busy) return;
      // 清空 value 允许重复选择同一批文件
      this.$refs.fileInput.value = '';
      this.$refs.fileInput.click();
    }

    async handleFilesChange(e) {
      const files = Array.from(e.target.files || []);
      if (!files.length || this.busy) return;
      this.processingFiles = true;
      try {
        const accepted = [];
        for (const file of files) {
          if (!ALLOWED_TYPES.includes(file.type)) {
            this.$message.warning(`${file.name}：仅支持 JPG/PNG/GIF/WebP，已跳过`);
            continue;
          }
          if (file.size > MAX_FILE_SIZE) {
            this.$message.warning(`${file.name}：超过 20MB，已跳过`);
            continue;
          }
          // 实际解码预检，拒绝伪装成图片的文件
          if (!await this.checkImageDecodable(file)) {
            this.$message.warning(`${file.name}：无法识别为有效图片，已跳过`);
            continue;
          }
          accepted.push({ file, status: 'pending', url: '', error: '' });
        }
        if (!accepted.length) return;
        if (this.items.length + accepted.length > MAX_BATCH_SIZE) {
          this.$message.warning(`一次最多上传 ${MAX_BATCH_SIZE} 张`);
          return;
        }
        const totalSize = [...this.items.map(i => i.file), ...accepted.map(i => i.file)]
          .reduce((sum, file) => sum + file.size, 0);
        if (totalSize > MAX_TOTAL_SIZE) {
          this.$message.warning(`一批图片总大小不能超过 ${this.formatSize(MAX_TOTAL_SIZE)}`);
          return;
        }
        this.items = this.items.concat(accepted);
      } finally {
        this.processingFiles = false;
      }
    }

    checkImageDecodable(file) {
      return new Promise(resolve => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        const timer = setTimeout(() => finish(false), DECODE_TIMEOUT);
        function finish(ok) {
          clearTimeout(timer);
          URL.revokeObjectURL(url);
          resolve(ok);
        }
        img.onload = () => finish(true);
        img.onerror = () => finish(false);
        img.src = url;
      });
    }

    removeItem(index) {
      if (this.busy) return;
      const item = this.sortedItems[index];
      this.items = this.items.filter(i => i !== item);
    }

    formatSize(size) {
      if (size >= 1024 * 1024) return `${(size / 1024 / 1024).toFixed(1)}MB`;
      return `${Math.ceil(size / 1024)}KB`;
    }

    // 防注入：blob_url 必须是 https 且不含可破坏 HTML 属性/markdown 语法的字符。
    // markdown 模式下圆括号（来自文件名，如“月报(修订).png”）不拒绝，统一百分号编码，
    // 避免上传已成功却因 URL 校验失败而重传制造孤儿 blob。
    assertValidBlobUrl(url) {
      if (typeof url !== 'string' || !url.startsWith('https://') || /[\s"<>\\]/.test(url)) {
        throw new Error('上传返回的图片地址异常');
      }
      if (this.format === 'markdown') {
        return url.replace(/\(/g, '%28').replace(/\)/g, '%29');
      }
      return url;
    }

    handleEditorChange(content) {
      this.$emit('change', content);
    }

    async uploadAndInsert() {
      if (this.busy || !this.items.length) return;
      this.uploading = true;
      try {
        const extraBody = _.get(this.$vadminConfig, 'ckeditor.directUploadExtraBody', {});
        // 冻结插入批次快照：正文只由这一批生成，成功后也只移除这一批，
        // 不触碰快照之外可能新加入列表的文件
        const insertionBatch = this.sortedItems.slice();
        const needUpload = insertionBatch.filter(item => item.status !== 'done');
        for (const item of needUpload) {
          item.status = 'uploading';
          try {
            const result = await uploadFile(item.file, extraBody);
            // result.url 是 PUT 签名地址，不是展示地址，绝不能回退使用
            item.url = this.assertValidBlobUrl(result.blob_url);
            item.status = 'done';
          } catch (err) {
            item.status = 'failed';
            item.error = err.message || String(err);
            this.$message.error(`${item.file.name} 上传失败：${item.error}。已成功的不会重传，可再次点击按钮重试`);
            return;
          }
        }
        // 全部成功后才生成插入内容，文件名不进入正文（防 markdown 语法注入与文件名泄露）
        const insertion = insertionBatch.map(item => {
          if (item.status !== 'done' || !item.url) {
            throw new Error(`${item.file.name} 缺少有效的图片地址，已中止插入`);
          }
          if (this.format === 'markdown') return `![](${item.url})`;
          return `<p><img src="${item.url}" /></p>`;
        }).join('\n');
        this.$emit('change', `${this.normalizedValue}\n${insertion}`);
        const count = insertionBatch.length;
        this.items = this.items.filter(i => !insertionBatch.includes(i));
        this.$message.success(`已上传并插入 ${count} 张图片`);
      } finally {
        this.uploading = false;
      }
    }
  }
</script>

<style scoped>
  .batch-insert-bar {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px 0;
    margin-bottom: 10px;
  }

  .pending-file-list {
    margin: 0 0 10px;
    padding: 8px 12px;
    list-style: none;
    border: 1px solid #ebeef5;
    border-radius: 4px;
  }

  .pending-file-list li {
    display: flex;
    align-items: center;
    line-height: 24px;
  }

  .file-order {
    color: #909399;
    margin-right: 6px;
  }

  .file-name {
    word-break: break-all;
  }

  .file-size {
    color: #909399;
    margin-left: 6px;
  }

  .file-status {
    margin-left: 8px;
    font-size: 12px;
  }

  .file-status.uploading {
    color: #e6a23c;
  }

  .file-status.done {
    color: #67c23a;
  }

  .file-status.failed {
    color: #f56c6c;
  }
</style>
